/**
 * captureFingerprint — the client capture-integrity signal collector (deepfake layer 2 of 6;
 * AI_DESIGN §0.2 layer 2 · injection / virtual-camera detection, OWN).
 *
 * Collects, around getUserMedia, what the *client-side capture surface reported about itself* — device
 * enumeration labels, the active video track's getSettings()/getCapabilities(), MediaStreamTrack state, and
 * best-effort frame-timing statistics — and shapes it as the versioned `ClientCaptureFingerprint` the be
 * forwards to the vCIP_ai own-injection-fingerprint scorer.
 *
 * HONESTY (binding, AI_DESIGN §0.2): every field here is CLIENT-REPORTED and attacker-influenced by
 * construction — a modified client can lie about all of it. This is NOT proof of camera provenance; it is a
 * probabilistic consistency signal that raises an attacker's cost and catches commodity tooling (OBS-style
 * virtual cameras, naive replays). The hard guarantee is the attested-capture roadmap (native app + hardware
 * attestation), stated as future. This module NEVER decides "virtual camera"; it only collects. The lexicon
 * + scoring live server-side in vCIP_ai, so the client ships no verdict and no tunable list.
 *
 * PII: device labels + UA strings are DEVICE metadata, not customer PII. This collector reads NO frames, NO
 * pixels, NO biometrics, NO location. It NEVER stops the caller's tracks (it only observes).
 *
 * Contract note: `ClientCaptureFingerprint` is the AI-plane-local shape (vCIP_ai
 * deepfake/fingerprint_contract.py). It is NOT in @vcip/contracts — promoting it into vCIP_shared_4.0 is a
 * human-owned cross-repo review; this TS interface is a documented client-local mirror, kept field-for-field
 * with the Python contract (camelCase, matching the browser APIs it mirrors).
 */

export const FINGERPRINT_SCHEMA_VERSION = '1.0';

export interface MediaDeviceEntry {
  readonly kind: string;
  readonly label: string;
  readonly deviceId: string;
  readonly groupId: string;
}

export interface MediaDevicesSnapshot {
  readonly devices: readonly MediaDeviceEntry[];
  readonly capturedAfterPermission: boolean;
}

export interface NumericRange {
  readonly min?: number;
  readonly max?: number;
}

export interface TrackCapabilities {
  readonly reportedKeys: readonly string[];
  readonly width?: NumericRange;
  readonly height?: NumericRange;
  readonly frameRate?: NumericRange;
  readonly facingModes: readonly string[];
  readonly torchSupported?: boolean | null;
  readonly zoomSupported?: boolean | null;
}

export interface TrackSettings {
  readonly width?: number;
  readonly height?: number;
  readonly frameRate?: number;
  readonly facingMode?: string;
  readonly deviceId?: string;
  readonly groupId?: string;
}

export interface TrackState {
  readonly label: string;
  readonly readyState: string;
  readonly muted: boolean;
  readonly muteToggleCount: number;
  readonly getUserMediaPatched?: boolean | null;
}

export interface FrameTimingStats {
  readonly sampleCount: number;
  readonly meanIntervalMs?: number | null;
  readonly stddevIntervalMs?: number | null;
  readonly minIntervalMs?: number | null;
  readonly maxIntervalMs?: number | null;
  readonly jitterMs?: number | null;
  readonly histogram: Record<string, number>;
}

export interface ClientHints {
  readonly userAgent: string;
  readonly platform: string;
  readonly isMobile?: boolean | null;
}

export interface ClientCaptureFingerprint {
  readonly schemaVersion: string;
  readonly nonce: string;
  readonly collectedAt: string;
  readonly mediaDevices: MediaDevicesSnapshot;
  readonly trackSettings: TrackSettings;
  readonly trackCapabilities: TrackCapabilities;
  readonly trackState: TrackState;
  readonly frameTiming: FrameTimingStats;
  readonly clientHints: ClientHints;
}

export interface CollectOptions {
  /** The nonce the server issued for this capture (`POST /capture/start`) — the be judges only a fingerprint echoing it. */
  readonly nonce: string;
  /** Max frames to sample for timing before stopping (default 90). */
  readonly maxTimingFrames?: number;
  /** Hard cap on the timing sample window in ms (default 2000). */
  readonly maxTimingMs?: number;
}

// requestVideoFrameCallback is not in the base DOM lib everywhere — declare the shape we use.
interface VideoFrameCallbackMetadataLike {
  readonly presentationTime?: number;
  readonly expectedDisplayTime?: number;
}
type RvfcVideo = HTMLVideoElement & {
  requestVideoFrameCallback?: (
    cb: (now: number, metadata: VideoFrameCallbackMetadataLike) => void,
  ) => number;
};
type UaDataLike = { readonly mobile?: boolean; readonly platform?: string };

/** True when getUserMedia/enumerateDevices are NOT native code (a monkey-patch tell — suppressible). */
function detectGumPatched(): boolean | null {
  try {
    const md = navigator.mediaDevices as unknown as {
      getUserMedia?: unknown;
      enumerateDevices?: unknown;
    };
    const isNative = (fn: unknown): boolean =>
      typeof fn === 'function' && Function.prototype.toString.call(fn).includes('[native code]');
    const gum = md?.getUserMedia;
    const enumerate = md?.enumerateDevices;
    if (!gum && !enumerate) return null;
    // Patched if either core method is present but not native code.
    return (gum !== undefined && !isNative(gum)) || (enumerate !== undefined && !isNative(enumerate));
  } catch {
    return null; // honest "not detectable", never coerced to a clean false
  }
}

function toRange(range: { min?: number; max?: number } | undefined): NumericRange | undefined {
  if (!range) return undefined;
  const out: { min?: number; max?: number } = {};
  if (typeof range.min === 'number') out.min = range.min;
  if (typeof range.max === 'number') out.max = range.max;
  return out;
}

function readCapabilities(track: MediaStreamTrack): TrackCapabilities {
  // getCapabilities is not universal (older Firefox lacks it) — an empty set is a signal, never an error.
  const getCaps = (track as MediaStreamTrack & { getCapabilities?: () => MediaTrackCapabilities })
    .getCapabilities;
  if (typeof getCaps !== 'function') {
    return { reportedKeys: [], facingModes: [] };
  }
  let caps: MediaTrackCapabilities;
  try {
    caps = getCaps.call(track);
  } catch {
    return { reportedKeys: [], facingModes: [] };
  }
  const capsRecord = caps as MediaTrackCapabilities & {
    facingMode?: string[];
    torch?: boolean;
    zoom?: unknown;
  };
  const out: {
    reportedKeys: string[];
    width?: NumericRange;
    height?: NumericRange;
    frameRate?: NumericRange;
    facingModes: string[];
    torchSupported?: boolean | null;
    zoomSupported?: boolean | null;
  } = {
    reportedKeys: Object.keys(caps),
    facingModes: Array.isArray(capsRecord.facingMode) ? capsRecord.facingMode : [],
    torchSupported: 'torch' in caps ? Boolean(capsRecord.torch) : null,
    zoomSupported: 'zoom' in caps ? true : null,
  };
  const width = toRange(caps.width);
  if (width) out.width = width;
  const height = toRange(caps.height);
  if (height) out.height = height;
  const frameRate = toRange(caps.frameRate);
  if (frameRate) out.frameRate = frameRate;
  return out;
}

function readSettings(track: MediaStreamTrack): TrackSettings {
  const s = track.getSettings();
  const out: {
    width?: number;
    height?: number;
    frameRate?: number;
    facingMode?: string;
    deviceId?: string;
    groupId?: string;
  } = {};
  if (typeof s.width === 'number') out.width = s.width;
  if (typeof s.height === 'number') out.height = s.height;
  if (typeof s.frameRate === 'number') out.frameRate = s.frameRate;
  if (typeof s.facingMode === 'string') out.facingMode = s.facingMode;
  if (typeof s.deviceId === 'string') out.deviceId = s.deviceId;
  if (typeof s.groupId === 'string') out.groupId = s.groupId;
  return out;
}

async function enumerate(): Promise<MediaDevicesSnapshot> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return {
      // capturedAfterPermission: this collector runs post-getUserMedia, so labels should be populated;
      // all-empty labels here are themselves a signal the server scores (label suppression / tampering).
      capturedAfterPermission: true,
      devices: devices.map((d) => ({
        kind: d.kind,
        label: d.label ?? '',
        deviceId: d.deviceId ?? '',
        groupId: d.groupId ?? '',
      })),
    };
  } catch {
    return { devices: [], capturedAfterPermission: true };
  }
}

/**
 * Best-effort inter-frame timing: sample requestVideoFrameCallback delivery timestamps off a detached,
 * muted video element and reduce to mean/stddev/min/max/jitter. Real sensors jitter; canvas/file replays and
 * loopback feeds are metronomic. Bounded by frame count AND wall-clock so it never delays the flow; if rVFC
 * is unavailable it returns sampleCount 0 (the scorer then marks frame_timing NOT EVALUATED — never a free
 * pass). NEVER stops the caller's tracks.
 */
async function sampleFrameTiming(
  stream: MediaStream,
  maxFrames: number,
  maxMs: number,
): Promise<FrameTimingStats> {
  const empty: FrameTimingStats = { sampleCount: 0, histogram: {} };
  if (typeof document === 'undefined') return empty;
  const video = document.createElement('video') as RvfcVideo;
  if (typeof video.requestVideoFrameCallback !== 'function') return empty;

  video.muted = true;
  video.playsInline = true;
  video.srcObject = stream;

  const timestamps: number[] = [];
  const detach = (): void => {
    try {
      video.pause();
    } catch {
      /* ignore */
    }
    video.srcObject = null; // detach WITHOUT stopping the caller's tracks
  };

  try {
    await video.play().catch(() => undefined);
    await new Promise<void>((resolve) => {
      const deadline = (typeof performance !== 'undefined' ? performance.now() : Date.now()) + maxMs;
      const onFrame = (now: number): void => {
        timestamps.push(now);
        const clock = typeof performance !== 'undefined' ? performance.now() : Date.now();
        if (timestamps.length >= maxFrames || clock >= deadline) {
          resolve();
          return;
        }
        video.requestVideoFrameCallback?.(onFrame);
      };
      video.requestVideoFrameCallback?.(onFrame);
      // Safety net: if frames never arrive, resolve at the deadline anyway.
      setTimeout(resolve, maxMs + 50);
    });
  } finally {
    detach();
  }

  const intervals: number[] = [];
  for (let i = 1; i < timestamps.length; i += 1) {
    const cur = timestamps[i];
    const prev = timestamps[i - 1];
    if (cur !== undefined && prev !== undefined) intervals.push(cur - prev);
  }
  if (intervals.length === 0) return empty;

  const n = intervals.length;
  const mean = intervals.reduce((a, b) => a + b, 0) / n;
  const variance = intervals.reduce((a, b) => a + (b - mean) ** 2, 0) / n;
  const stddev = Math.sqrt(variance);
  let jitterSum = 0;
  for (let i = 1; i < intervals.length; i += 1) {
    const cur = intervals[i];
    const prev = intervals[i - 1];
    if (cur !== undefined && prev !== undefined) jitterSum += Math.abs(cur - prev);
  }
  const jitter = intervals.length > 1 ? jitterSum / (intervals.length - 1) : 0;

  return {
    sampleCount: n,
    meanIntervalMs: round(mean),
    stddevIntervalMs: round(stddev),
    minIntervalMs: round(Math.min(...intervals)),
    maxIntervalMs: round(Math.max(...intervals)),
    jitterMs: round(jitter),
    histogram: {},
  };
}

function round(v: number): number {
  return Math.round(v * 1000) / 1000;
}

/**
 * Collect the full layer-2 fingerprint from a live capture stream. Returns null when there is no live video
 * track to observe (nothing honest to score). Never throws for a missing optional API — each collector
 * degrades to an empty/"not detectable" value the server scores as a signal, not an error.
 */
export async function collectCaptureFingerprint(
  stream: MediaStream | null | undefined,
  options: CollectOptions,
): Promise<ClientCaptureFingerprint | null> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices) return null;
  const track = stream?.getVideoTracks?.()[0];
  if (!track) return null;

  const uaData = (navigator as Navigator & { userAgentData?: UaDataLike }).userAgentData;
  const isMobile =
    typeof uaData?.mobile === 'boolean'
      ? uaData.mobile
      : /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent ?? '');

  const [mediaDevices, frameTiming] = await Promise.all([
    enumerate(),
    sampleFrameTiming(stream as MediaStream, options.maxTimingFrames ?? 90, options.maxTimingMs ?? 2000),
  ]);

  return {
    schemaVersion: FINGERPRINT_SCHEMA_VERSION,
    nonce: options.nonce,
    collectedAt: new Date().toISOString(),
    mediaDevices,
    trackSettings: readSettings(track),
    trackCapabilities: readCapabilities(track),
    trackState: {
      label: track.label ?? '',
      readyState: track.readyState ?? 'live',
      muted: Boolean(track.muted),
      muteToggleCount: 0,
      getUserMediaPatched: detectGumPatched(),
    },
    frameTiming,
    clientHints: {
      userAgent: navigator.userAgent ?? '',
      platform: uaData?.platform ?? (navigator as Navigator & { platform?: string }).platform ?? '',
      isMobile,
    },
  };
}
